/**
 * Teacher Management Store
 * Zustand store for managing teacher-related state
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { teachApi } from '../services/teachApi';
import type {
  Student,
  StudentDetail,
  StudentFilters,
  Class,
  ClassMember,
  CreateClassRequest,
  UpdateClassRequest
} from '@shared/types/teach';

interface TeachState {
  // Student state
  students: Student[];
  studentDetail: StudentDetail | null;
  studentsPagination: {
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  } | null;
  
  // Class state
  classes: Class[];
  selectedClass: Class | null;
  classMembers: ClassMember[];
  
  // Loading states
  loading: boolean;
  studentsLoading: boolean;
  classesLoading: boolean;
  membersLoading: boolean;
  
  // Error state
  error: string | null;
  
  // ============= Student Actions =============
  
  fetchStudents: (filters?: StudentFilters) => Promise<void>;
  fetchStudentDetail: (studentId: string) => Promise<void>;
  clearStudentDetail: () => void;
  
  // ============= Class Actions =============
  
  fetchClasses: () => Promise<void>;
  createClass: (data: CreateClassRequest) => Promise<Class>;
  updateClass: (classId: string, data: UpdateClassRequest) => Promise<void>;
  deleteClass: (classId: string) => Promise<void>;
  setSelectedClass: (cls: Class | null) => void;
  
  // ============= Class Member Actions =============
  
  fetchClassMembers: (classId: string) => Promise<void>;
  addMembersToClass: (classId: string, studentIds: string[]) => Promise<void>;
  removeMemberFromClass: (classId: string, studentId: string) => Promise<void>;
  clearClassMembers: () => void;
  
  // ============= Utility Actions =============
  
  clearError: () => void;
  reset: () => void;
}

const initialState = {
  students: [],
  studentDetail: null,
  studentsPagination: null,
  classes: [],
  selectedClass: null,
  classMembers: [],
  loading: false,
  studentsLoading: false,
  classesLoading: false,
  membersLoading: false,
  error: null,
};

export const useTeachStore = create<TeachState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      // ============= Student Actions =============

      fetchStudents: async (filters?: StudentFilters) => {
        set({ studentsLoading: true, error: null });
        try {
          const response = await teachApi.getStudents(filters);
          set({
            students: response.data,
            studentsPagination: response.pagination,
            studentsLoading: false,
          });
        } catch (error: any) {
          set({
            error: error.message || 'Failed to fetch students',
            studentsLoading: false,
          });
          throw error;
        }
      },

      fetchStudentDetail: async (studentId: string) => {
        set({ loading: true, error: null });
        try {
          const detail = await teachApi.getStudentDetail(studentId);
          set({
            studentDetail: detail,
            loading: false,
          });
        } catch (error: any) {
          set({
            error: error.message || 'Failed to fetch student detail',
            loading: false,
          });
          throw error;
        }
      },

      clearStudentDetail: () => {
        set({ studentDetail: null });
      },

      // ============= Class Actions =============

      fetchClasses: async () => {
        set({ classesLoading: true, error: null });
        try {
          const response = await teachApi.getClasses();
          set({
            classes: response.classes,
            classesLoading: false,
          });
        } catch (error: any) {
          set({
            error: error.message || 'Failed to fetch classes',
            classesLoading: false,
          });
          throw error;
        }
      },

      createClass: async (data: CreateClassRequest) => {
        set({ loading: true, error: null });
        try {
          const response = await teachApi.createClass(data);
          const newClass = response.class;
          
          set((state) => ({
            classes: [...state.classes, newClass],
            loading: false,
          }));
          
          return newClass;
        } catch (error: any) {
          set({
            error: error.message || 'Failed to create class',
            loading: false,
          });
          throw error;
        }
      },

      updateClass: async (classId: string, data: UpdateClassRequest) => {
        set({ loading: true, error: null });
        try {
          await teachApi.updateClass(classId, data);
          
          set((state) => ({
            classes: state.classes.map((cls) =>
              cls.id === classId ? { ...cls, ...data } : cls
            ),
            selectedClass:
              state.selectedClass?.id === classId
                ? { ...state.selectedClass, ...data }
                : state.selectedClass,
            loading: false,
          }));
        } catch (error: any) {
          set({
            error: error.message || 'Failed to update class',
            loading: false,
          });
          throw error;
        }
      },

      deleteClass: async (classId: string) => {
        set({ loading: true, error: null });
        try {
          await teachApi.deleteClass(classId);
          
          set((state) => ({
            classes: state.classes.filter((cls) => cls.id !== classId),
            selectedClass:
              state.selectedClass?.id === classId ? null : state.selectedClass,
            loading: false,
          }));
        } catch (error: any) {
          set({
            error: error.message || 'Failed to delete class',
            loading: false,
          });
          throw error;
        }
      },

      setSelectedClass: (cls: Class | null) => {
        set({ selectedClass: cls });
      },

      // ============= Class Member Actions =============

      fetchClassMembers: async (classId: string) => {
        set({ membersLoading: true, error: null });
        try {
          const response = await teachApi.getClassMembers(classId);
          set({
            classMembers: response.members,
            membersLoading: false,
          });
        } catch (error: any) {
          set({
            error: error.message || 'Failed to fetch class members',
            membersLoading: false,
          });
          throw error;
        }
      },

      addMembersToClass: async (classId: string, studentIds: string[]) => {
        set({ loading: true, error: null });
        try {
          await teachApi.addMembersToClass(classId, studentIds);
          
          // Refresh class members
          await get().fetchClassMembers(classId);
          
          // Update member count in classes list
          set((state) => ({
            classes: state.classes.map((cls) =>
              cls.id === classId
                ? { ...cls, memberCount: cls.memberCount + studentIds.length }
                : cls
            ),
            loading: false,
          }));
        } catch (error: any) {
          set({
            error: error.message || 'Failed to add members to class',
            loading: false,
          });
          throw error;
        }
      },

      removeMemberFromClass: async (classId: string, studentId: string) => {
        set({ loading: true, error: null });
        try {
          await teachApi.removeMemberFromClass(classId, studentId);
          
          // Remove member from local state
          set((state) => ({
            classMembers: state.classMembers.filter((m) => m.id !== studentId),
            classes: state.classes.map((cls) =>
              cls.id === classId
                ? { ...cls, memberCount: Math.max(0, cls.memberCount - 1) }
                : cls
            ),
            loading: false,
          }));
        } catch (error: any) {
          set({
            error: error.message || 'Failed to remove member from class',
            loading: false,
          });
          throw error;
        }
      },

      clearClassMembers: () => {
        set({ classMembers: [] });
      },

      // ============= Utility Actions =============

      clearError: () => {
        set({ error: null });
      },

      reset: () => {
        set(initialState);
      },
    }),
    {
      name: 'teach-store',
    }
  )
);
